import { useId, useState } from 'react'
import { Box, Card, Collapse, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

/**
 * Card section with a clickable header that expands/collapses content.
 * Starts collapsed by default. Content stays mounted so form/table state is kept.
 */
export default function CollapsibleSection({
  title,
  defaultExpanded = false,
  headerExtra = null,
  children,
  sx = {},
  contentSx = {},
  mb = 3,
  onExpandedChange,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const panelId = useId()
  const headerId = `${panelId}-header`

  const toggle = () =>
    setExpanded((prev) => {
      const next = !prev
      onExpandedChange?.(next)
      return next
    })

  return (
    <Card
      sx={{
        mb,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        ...sx,
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        id={headerId}
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggle()
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: { xs: 1, sm: 2 },
          flexWrap: 'wrap',
          width: '100%',
          minWidth: 0,
          p: { xs: 2, sm: 3 },
          pb: expanded ? { xs: 1.5, sm: 2 } : { xs: 2, sm: 3 },
          cursor: 'pointer',
          userSelect: 'none',
          WebkitTapHighlightColor: 'transparent',
          '&:focus-visible': {
            outline: '2px solid #1976d2',
            outlineOffset: -2,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            flex: '1 1 auto',
          }}
        >
          <Typography
            variant="h6"
            component="span"
            sx={{
              fontWeight: 600,
              minWidth: 0,
              wordBreak: 'break-word',
            }}
          >
            {title}
          </Typography>
          <ExpandMoreIcon
            aria-hidden
            sx={{
              flexShrink: 0,
              color: '#666',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          />
        </Box>
        {headerExtra ? (
          <Box
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{
              flex: '0 1 auto',
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            {headerExtra}
          </Box>
        ) : null}
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
        <Box
          id={panelId}
          role="region"
          aria-labelledby={headerId}
          sx={{
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 },
            pt: 0,
            width: '100%',
            minWidth: 0,
            overflowX: 'auto',
            ...contentSx,
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Card>
  )
}
